import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { apiClient } from "@/services/api/client";
import type { SearchResult } from "@/services/api/types";

export interface SymbolSearchInputProps {
  value: string;
  onValueChange: (value: string) => void;
  onSubmitSymbol: (symbol: string, option?: SearchResult) => Promise<void> | void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  maxResults?: number;
  disabled?: boolean;
  autoFocus?: boolean;
}

export function SymbolSearchInput({
  value,
  onValueChange,
  onSubmitSymbol,
  placeholder = "代码/名称",
  className,
  inputClassName,
  dropdownClassName,
  maxResults = 8,
  disabled = false,
  autoFocus = false,
}: SymbolSearchInputProps) {
  const [options, setOptions] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const requestSeq = useRef(0);
  const closeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const query = value.trim();
    if (!query) {
      setOptions([]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const raw = await apiClient.searchSymbols(query);
        if (requestSeq.current !== seq) {
          return;
        }
        const ranked = rankSymbolCandidates(query, raw).slice(0, maxResults);
        setOptions(ranked);
        setOpen(focused);
        setActiveIndex(ranked.length ? 0 : -1);
      } catch {
        if (requestSeq.current === seq) {
          setOptions([]);
          setOpen(focused);
          setActiveIndex(-1);
        }
      } finally {
        if (requestSeq.current === seq) {
          setLoading(false);
        }
      }
    }, 140);

    return () => window.clearTimeout(timer);
  }, [focused, maxResults, value]);

  async function submitRawSymbol() {
    const symbol = normalizeRawSymbol(value);
    if (!symbol) {
      return;
    }
    await onSubmitSymbol(symbol);
    setOpen(false);
  }

  async function submitOption(option: SearchResult) {
    const symbol = readOptionSymbol(option);
    if (!symbol) {
      return;
    }
    onValueChange(symbol);
    await onSubmitSymbol(symbol, option);
    setOpen(false);
  }

  return (
    <div className={`relative ${className || ""}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-slate-500" />
      <Input
        autoFocus={autoFocus}
        disabled={disabled}
        value={value}
        onChange={(event) => {
          onValueChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setFocused(true);
          if (options.length) {
            setOpen(true);
          }
        }}
        onBlur={() => {
          setFocused(false);
          if (closeTimerRef.current != null) {
            window.clearTimeout(closeTimerRef.current);
          }
          closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            if (!options.length) {
              return;
            }
            setOpen(true);
            setActiveIndex((prev) => (prev + 1) % options.length);
            return;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            if (!options.length) {
              return;
            }
            setOpen(true);
            setActiveIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
            return;
          }
          if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
            return;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            if (open && activeIndex >= 0 && activeIndex < options.length) {
              void submitOption(options[activeIndex]);
              return;
            }
            void submitRawSymbol();
          }
        }}
        className={`h-8 rounded-md border-[var(--border)] bg-[#0c0f14] pl-9 text-sm ${inputClassName || ""}`}
        placeholder={placeholder}
      />
      {open ? (
        <div
          className={`absolute left-0 right-0 top-10 z-30 rounded-md border border-[var(--border-strong)] bg-[#0f1217] p-1.5 shadow-2xl ${dropdownClassName || ""}`}
        >
          {loading ? <div className="px-3 py-2 text-xs text-[var(--muted)]">搜索中...</div> : null}
          {!loading && !options.length ? <div className="px-3 py-2 text-xs text-[var(--muted)]">暂无匹配</div> : null}
          {!loading
            ? options.map((option, index) => {
                const symbol = readOptionSymbol(option);
                const name = String(option.name || option.symbol || option.ts_code || "");
                const active = index === activeIndex;
                return (
                  <button
                    key={`${symbol}:${name}:${index}`}
                    className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition ${
                      active ? "bg-white/10 text-white" : "text-slate-200 hover:bg-white/6"
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      void submitOption(option);
                    }}
                  >
                    <span>{symbol || "--"}</span>
                    <span className="truncate pl-3 text-slate-500">{name || "--"}</span>
                  </button>
                );
              })
            : null}
        </div>
      ) : null}
    </div>
  );
}

function normalizeRawSymbol(raw: string) {
  return raw.trim().toUpperCase();
}

function readOptionSymbol(option: SearchResult) {
  return normalizeRawSymbol(String(option.ts_code || option.symbol || ""));
}

function normalizeForMatch(raw: string) {
  return raw.toUpperCase().replace(/\s+/g, "").replace(/[^A-Z0-9]/g, "");
}

function subsequenceScore(query: string, target: string) {
  if (!query || !target) {
    return 0;
  }
  let qi = 0;
  for (let ti = 0; ti < target.length && qi < query.length; ti += 1) {
    if (target[ti] === query[qi]) {
      qi += 1;
    }
  }
  if (qi !== query.length) {
    return 0;
  }
  return query.length / target.length;
}

function candidateScore(query: string, option: SearchResult) {
  const symbol = readOptionSymbol(option);
  const name = String(option.name || "");
  const symbolNorm = normalizeForMatch(symbol);
  const nameNorm = normalizeForMatch(name);
  const queryNorm = normalizeForMatch(query);
  const queryUpper = query.trim().toUpperCase();

  let score = 0;
  if (symbol === queryUpper) score += 1200;
  if (symbol.startsWith(queryUpper)) score += 900;
  if (symbol.includes(queryUpper)) score += 700;
  if (name.toUpperCase().startsWith(queryUpper)) score += 560;
  if (name.toUpperCase().includes(queryUpper)) score += 420;
  if (symbolNorm.startsWith(queryNorm)) score += 380;
  if (symbolNorm.includes(queryNorm)) score += 250;
  if (nameNorm.includes(queryNorm)) score += 180;
  score += Math.round(subsequenceScore(queryNorm, symbolNorm) * 140);
  score += Math.round(subsequenceScore(queryNorm, nameNorm) * 80);

  return score;
}

function rankSymbolCandidates(query: string, options: SearchResult[]) {
  const deduped = new Map<string, SearchResult>();
  options.forEach((option) => {
    const symbol = readOptionSymbol(option);
    if (!symbol || deduped.has(symbol)) {
      return;
    }
    deduped.set(symbol, option);
  });

  return Array.from(deduped.values())
    .map((option) => ({
      option,
      symbol: readOptionSymbol(option),
      score: candidateScore(query, option),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      return a.symbol.localeCompare(b.symbol);
    })
    .map((entry) => entry.option);
}
